/**
 * Guest Session Manager
 * 
 * Saves guest player progress to localStorage so it can be migrated
 * to an authenticated account later.
 */

const GUEST_SESSION_KEY = 'grandline_guest_session';
const GUEST_CHARACTER_ID_KEY = 'grandline_guest_character_id';
const GUEST_ID_KEY = 'grandline_guest_id';

export interface GuestSessionData {
  name: string;
  characterType: string;
  shipColor: string;
  position: { x: number; y: number };
  berries: number;
  level: number;
  xp: number;
  infamy: number;
  skills: Record<string, { level: number; xp: number; totalXp: number }>;
  inventory: unknown[];
  lastSavedAt: number;
}

/**
 * Save guest session data to localStorage
 */
export const saveGuestSession = (data: Partial<GuestSessionData>): void => {
  try {
    const existing = loadGuestSession();
    const merged: GuestSessionData = {
      name: data.name ?? existing?.name ?? 'Guest',
      characterType: data.characterType ?? existing?.characterType ?? 'pirate',
      shipColor: data.shipColor ?? existing?.shipColor ?? '#3498db',
      position: data.position ?? existing?.position ?? { x: 500, y: 500 },
      berries: data.berries ?? existing?.berries ?? 0,
      level: data.level ?? existing?.level ?? 1,
      xp: data.xp ?? existing?.xp ?? 0,
      infamy: data.infamy ?? existing?.infamy ?? 0,
      skills: data.skills ?? existing?.skills ?? {},
      inventory: data.inventory ?? existing?.inventory ?? [],
      lastSavedAt: Date.now(),
    };
    localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(merged));
  } catch (error) {
    console.error('Failed to save guest session:', error);
  }
};

export const getOrCreateGuestId = (): string => {
  try {
    const existing: string | null = localStorage.getItem(GUEST_ID_KEY);
    if (existing && existing.length > 0) {
      return existing;
    }

    const cryptoObj = (globalThis as unknown as { crypto?: Crypto }).crypto;
    const newId: string = cryptoObj?.randomUUID
      ? cryptoObj.randomUUID()
      : `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    localStorage.setItem(GUEST_ID_KEY, newId);
    return newId;
  } catch {
    return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

/**
 * Load guest session data from localStorage
 */
export const loadGuestSession = (): GuestSessionData | null => {
  try {
    const stored = localStorage.getItem(GUEST_SESSION_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as GuestSessionData;
  } catch (error) {
    console.error('Failed to load guest session:', error);
    return null;
  }
};

/**
 * Check if there's a valid guest session that can be migrated
 */
export const hasGuestSession = (): boolean => {
  const session = loadGuestSession();
  if (!session) return false;
  
  // Session is valid if it has meaningful progress (level > 1 or berries > 1000)
  return session.level > 1 || session.berries > 1000 || session.xp > 0;
};

/**
 * Get a summary of the guest session for display
 */
export const getGuestSessionSummary = (): { level: number; berries: number; playtime: string } | null => {
  const session = loadGuestSession();
  if (!session) return null;
  
  const ageMs = Date.now() - session.lastSavedAt;
  const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
  const ageDays = Math.floor(ageHours / 24);
  
  let playtime: string;
  if (ageDays > 0) {
    playtime = `${ageDays}d ago`;
  } else if (ageHours > 0) {
    playtime = `${ageHours}h ago`;
  } else {
    playtime = 'Recently';
  }
  
  return {
    level: session.level,
    berries: session.berries,
    playtime,
  };
};

/**
 * Clear guest session data (after successful migration or manual clear)
 */
export const clearGuestSession = (): void => {
  try {
    localStorage.removeItem(GUEST_SESSION_KEY);
  } catch (error) {
    console.error('Failed to clear guest session:', error);
  }
};

export const getOrCreateGuestCharacterId = (): string => {
  try {
    const existing: string | null = localStorage.getItem(GUEST_CHARACTER_ID_KEY);
    if (existing && existing.length > 0) {
      return existing;
    }

    const newId: string = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(GUEST_CHARACTER_ID_KEY, newId);
    return newId;
  } catch {
    return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

/**
 * Get guest session data for migration to a new character
 */
export const getGuestSessionForMigration = (): {
  berries: number;
  level: number;
  xp: number;
  infamy: number;
  skills: Record<string, { level: number; xp: number; totalXp: number }>;
  inventory: unknown[];
} | null => {
  const session = loadGuestSession();
  if (!session) return null;
  
  return {
    berries: session.berries,
    level: session.level,
    xp: session.xp,
    infamy: session.infamy,
    skills: session.skills,
    inventory: session.inventory,
  };
};
